import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import capitalize from 'lodash/capitalize'
import lowerCase from 'lodash/lowerCase'

import { toast } from 'react-toastify'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'

import { Menu, MenuItem, Typography, Tooltip, TextField, Box } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { isUnPaidInvoice } from 'utils/billing'
import { getRevertPaymentConfirmationMsg } from 'utils/payment'
import { PAYMENT_STATUS, PAYMENT_METHODS } from 'settings/constants/payment'
import { putIsLoading } from 'middleware/actions/response'
import { downloadPaymentReceiptBlobListInChunks } from 'utils/file'
import { useDeletePaymentBatchesFromGroupMutation } from 'api/groups/payment/deletePaymentBatchesFromGroup'
import { useRegeneratePaymentReceiptsMutation } from 'api/billing/regeneratePaymentReceipts'
import { getCustomerDetails } from 'middleware/actions/customers'
import {
  CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_PAYMENT_LIST,
  CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNT_DETAILS_BY_ID,
  CACHE_TAG_ACCOUNT_GROUP_DETAILS,
  CACHE_TAG_AGING_GROUP_DETAILS,
  CACHE_TAG_SUSPENSION_GROUP_DETAILS,
} from 'api/cacheTagTypes'
import { isCustomerDetailsPage } from 'router/routes'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import { BACKEND_DATE_FORMAT_DATE_FNS } from 'settings/constants/date'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import usePaymentProvider from 'components/customer-details/content/payment-details/usePaymentProvider'
import { checkIsPaymentLocked } from 'components/billing/common/lock-billing-date/utils'
import BillingLockedTooltip from 'components/billing/common/lock-billing-date/BillingLockedTooltip'

import T from 'T'
import api from 'api'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import TransferPaymentModal from 'components/customer-details/content/payments/transfer-payments'
import ACHFailedDialog from 'components/customer-details/content/payments/ach-failed'
import AddToGroupDialog from 'components/billing/groups/payment/AddToGroupDialog'
import { usePartialRefundPaymentMutation, useRefundPaymentMutation, useReversePaymentMutation, useVoidPaymentMutation } from 'api/billing/reversePayment'
import PaymentReceiptEmailConfirmation from './payment-receipt-email-confirmation'
import MoveToAccountCreditConfirmation from './MoveToAccountCreditConfirmation'
import AddUpdatePaymentNoteDialog from './AddUpdatePaymentNoteDialog'

const { APPROVED, DECLINED, FAILED, PROCESSED, PENDING } = PAYMENT_STATUS
const { CHECK, OTHER, CASH, ACH, CREDIT_CARD } = PAYMENT_METHODS

// A credit-card transaction can be voided only before it settles (~1 day). After settlement it must be refunded instead.
const CREDIT_CARD_VOID_WINDOW_MS = 24 * 60 * 60 * 1000

const PaymentsActionMenu = ({
  anchorEl,
  customerDetails = null,
  selectedPayment = {},
  isOriginalPaymentReversedOrTransferred = false,
  onClose,
  showRevertAction = true,
  showTransferAction = true,
  showACHFailedAction = true,
  showAddToGroupAction = true,
  showMoveToAccountCreditAction = true,
}) => {
  const dispatch = useDispatch()
  const businessId = useSelector(state => state.AuthReducer.userInfo.businessInfo.businessId, shallowEqual)
  const { isFortis } = usePaymentProvider(get(selectedPayment, 'accountId'))
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isConfirmRemovePaymentDialogOpen, setIsConfirmRemovePaymentDialogOpen] = useState(false)
  const [isOpenAddToGroupDialog, setIsOpenAddToGroupDialog] = useState(false)
  const [transferInvoiceDialog, setTransferInvoiceDialog] = useState({ isOpen: false, isIntraAccountTransfer: false })
  const [isACHDialogDialogOpen, setIsACHDialogDialogOpen] = useState(false)
  const [isMarkAsRefundedConfirmDialogOpen, setIsMarkAsRefundedConfirmDialogOpen] = useState(false)
  const [isCCRefundConfirmDialogOpen, setIsCCRefundConfirmDialogOpen] = useState(false)
  const [isOpenReceiptConfirmationDialog, setIsOpenReceiptConfirmationDialog] = useState(false)
  const [isOpenMoveToAccountCreditConfirmation, setIsOpenMoveToAccountCreditConfirmation] = useState(false)
  const [isOpenAddUpdatePaymentNoteDialog, setIsOpenAddUpdatePaymentNoteDialog] = useState(false)
  const [canDismissRevertDialog, setCanDismissRevertDialog] = useState(false)
  const [isPartialRefundConfirmDialogOpen, setIsPartialRefundConfirmDialogOpen] = useState(false)
  const [isVoidConfirmDialogOpen, setIsVoidConfirmDialogOpen] = useState(false)
  const [partialRefundAmount, setPartialRefundAmount] = useState('')

  // Mutations
  const [markPaymentAsReverted] = useReversePaymentMutation()
  const [partialRefundPayment] = usePartialRefundPaymentMutation()
  const [refundPayment] = useRefundPaymentMutation()
  const [voidPayment] = useVoidPaymentMutation()
  const [deletePaymentBatchesFromGroup] = useDeletePaymentBatchesFromGroupMutation()
  const [regeneratePaymentReceipts] = useRegeneratePaymentReceiptsMutation()

  // Memoized values
  const paymentNumber = useMemo(() => get(selectedPayment, 'paymentNumber'), [selectedPayment])
  const transactionStatus = useMemo(() => capitalize(get(selectedPayment, 'transactionStatus', '')), [selectedPayment])
  const paymentMethod = useMemo(() => get(selectedPayment, 'paymentMethod'), [selectedPayment])
  const paymentDate = useMemo(() => get(selectedPayment, 'paymentDate'), [selectedPayment])
  const transactionPostedDate = useMemo(() => get(selectedPayment, 'transactionPostedDate'), [selectedPayment])
  const { onlyDate: transactionDate } = convertDateTimeToPreferedTimezone({
    dateTime: transactionPostedDate,
    defaultDateFormat: BACKEND_DATE_FORMAT_DATE_FNS,
  })
  const { checkIsLocked } = useAccountingLockValue()
  const isLocked = useMemo(
    () => checkIsPaymentLocked(paymentDate, transactionDate, checkIsLocked),
    [paymentDate, transactionDate, checkIsLocked]
  )
  const isDuplicateActionDisabled = useMemo(
    () => isLocked || isOriginalPaymentReversedOrTransferred,
    [isLocked, isOriginalPaymentReversedOrTransferred]
  )
  const isDuplicateActionTooltipDisabled = useMemo(
    () => isLocked || !isOriginalPaymentReversedOrTransferred,
    [isLocked, isOriginalPaymentReversedOrTransferred]
  )
  const hasLinkedAccountCredit = useMemo(() => get(selectedPayment, 'accountCredit.amountCents', 0) > 0, [selectedPayment])
  const invoices = useMemo(() => get(selectedPayment, 'invoices', []), [selectedPayment])
  const linkedInvoices = useMemo(() => invoices.map(invoice => invoice?.invoiceNumber), [invoices])
  const hasPartialInvoice = useMemo(
    () =>
      invoices.find(
        invoice => isUnPaidInvoice(invoice.status) && invoice.amountPaidCents > 0 && invoice.amountPaidCents < invoice.totalCents
      ),
    [invoices]
  )

  const paymentTypeDisplay = useMemo(() => {
    const type = get(selectedPayment, 'paymentType', '') || get(selectedPayment, 'creditCardType', '')
    return type || paymentMethod
  }, [selectedPayment, paymentMethod])

  const lastFourDigits = useMemo(
    () => get(selectedPayment, 'lastFour', '') || get(selectedPayment, 'last4', '') || get(selectedPayment, 'accountLastFour', ''),
    [selectedPayment]
  )

  // Add state for the partial refund note (with the other useState declarations around line 68):
  const [partialRefundNote, setPartialRefundNote] = useState('')
  const isApproved = useMemo(() => transactionStatus === APPROVED, [transactionStatus])
  const isAchReturned = useMemo(() => [PROCESSED].includes(transactionStatus), [transactionStatus])
  const isRevertTransferBtnEnabled = useMemo(() => isApproved && [CHECK, OTHER, CASH].includes(paymentMethod), [isApproved, paymentMethod])
  const isMarkAsRevertedBtnEnabled = useMemo(() => isApproved && [CREDIT_CARD, ACH].includes(paymentMethod), [isApproved, paymentMethod])
  const isCCRefundBtnEnabled = useMemo(() => isApproved && [CREDIT_CARD, ACH].includes(paymentMethod), [isApproved, paymentMethod])
  const isMarkAsFailedBtnEnabledFortis = useMemo(
    () => isAchReturned && [ACH].includes(paymentMethod) && isFortis,
    [isAchReturned, isFortis, paymentMethod]
  )

  const revertMessage = useMemo(
    () => getRevertPaymentConfirmationMsg(hasLinkedAccountCredit, linkedInvoices, hasPartialInvoice),
    [hasLinkedAccountCredit, linkedInvoices, hasPartialInvoice]
  )
  const isMarkACHFailedBtnEnabled = useMemo(() => isApproved && paymentMethod === ACH, [isApproved, paymentMethod])
  const groupId = useMemo(() => get(selectedPayment, 'groupId'), [selectedPayment])
  const batchId = useMemo(() => get(selectedPayment, 'batchId'), [selectedPayment])
  const accountTransactionHistoryId = useMemo(() => get(selectedPayment, 'id'), [selectedPayment])
  const accountId = useMemo(() => get(selectedPayment, 'accountId'), [selectedPayment])
  const paymentNote = useMemo(() => get(selectedPayment, 'paymentNote', ''), [selectedPayment])
  const paymentAmountCents = useMemo(() => get(selectedPayment, 'transactionAmountCents', 0), [selectedPayment])

  // Partial Refund - Memoized values
  const totalRefundedCents = useMemo(() => get(selectedPayment, 'refundedAmountCents', 0), [selectedPayment])
  const hasPartialRefunds = useMemo(() => totalRefundedCents > 0, [totalRefundedCents])

  const remainingRefundableAmountCents = useMemo(() => paymentAmountCents - totalRefundedCents, [paymentAmountCents, totalRefundedCents])

  // Get available account credit from billingDetails (creditLeft)
  const availableAccountCreditCents = useMemo(() => {
    const creditLeftCents = get(customerDetails, 'customerCreditBalanceCents', 0)
    return Math.round(creditLeftCents)
  }, [customerDetails])

  // Maximum partial refund is constrained by BOTH remaining payment and available credit
  // This ensures we don't mess with invoice allocations - customer must create account credit first
  const maxPartialRefundCents = useMemo(
    () => Math.min(remainingRefundableAmountCents, availableAccountCreditCents),
    [remainingRefundableAmountCents, availableAccountCreditCents]
  )

  const maxPartialRefundAmount = useMemo(() => (maxPartialRefundCents / 100).toFixed(2), [maxPartialRefundCents])

  const isValidRefundAmount = useMemo(() => {
    const amount = parseFloat(partialRefundAmount)
    const maxAmount = maxPartialRefundCents / 100
    return !Number.isNaN(amount) && amount > 0 && amount <= maxAmount
  }, [partialRefundAmount, maxPartialRefundCents])

  // Helper to show specific error messages
  const refundAmountError = useMemo(() => {
    if (partialRefundAmount === '') return ''
    const amount = parseFloat(partialRefundAmount)
    if (Number.isNaN(amount)) return 'Please enter a valid amount'
    if (amount <= 0) return 'Amount must be greater than $0.00'
    if (amount > remainingRefundableAmountCents / 100) {
      return `Exceeds remaining payment amount ($${(remainingRefundableAmountCents / 100).toFixed(2)})`
    }
    if (amount > availableAccountCreditCents / 100) {
      return `Exceeds available account credit ($${(availableAccountCreditCents / 100).toFixed(2)})`
    }
    return ''
  }, [partialRefundAmount, remainingRefundableAmountCents, availableAccountCreditCents])

  const isPartialRefundEnabled = useMemo(
    () => isApproved && paymentMethod === CREDIT_CARD && maxPartialRefundCents > 0,
    [isApproved, paymentMethod, maxPartialRefundCents]
  )

  // Credit card: voidable only within CREDIT_CARD_VOID_WINDOW_HOURS of being posted. transactionPostedDate is a
  // backend LocalDateTime (no zone), so treat it as UTC — matching how the app interprets these elsewhere
  // (convertTimeToTimezone appends 'Z'). Comparing absolute instants makes this timezone-safe.
  const isWithinCreditCardVoidWindow = useMemo(() => {
    if (typeof transactionPostedDate !== 'string' || !transactionPostedDate) return false
    const postedUtc = new Date(transactionPostedDate.includes('Z') ? transactionPostedDate : `${transactionPostedDate}Z`)
    if (Number.isNaN(postedUtc.getTime())) return false
    return Date.now() - postedUtc.getTime() < CREDIT_CARD_VOID_WINDOW_MS
  }, [transactionPostedDate])

  const isVoidBtnEnabled = useMemo(
    () =>
      isFortis &&
      !hasPartialRefunds &&
      ((paymentMethod === ACH && (isApproved || transactionStatus === PENDING)) ||
        (paymentMethod === CREDIT_CARD && isApproved && isWithinCreditCardVoidWindow)),
    [isFortis, hasPartialRefunds, paymentMethod, isApproved, transactionStatus, isWithinCreditCardVoidWindow]
  )

  // Refs and callbacks
  const dismissTimerRef = useRef(null)
  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current)
      dismissTimerRef.current = null
    }
  }, [])

  // Effects
  useEffect(() => {
    if (isConfirmDialogOpen) {
      setCanDismissRevertDialog(false)
      clearDismissTimer()
      dismissTimerRef.current = setTimeout(() => {
        setCanDismissRevertDialog(true)
        dismissTimerRef.current = null
      }, 5000)
    } else {
      clearDismissTimer()
      setCanDismissRevertDialog(false)
    }

    return () => {
      clearDismissTimer()
    }
  }, [isConfirmDialogOpen, clearDismissTimer])

  // Handlers
  const invalidateAccountTransaction = () => {
    if (isCustomerDetailsPage()) {
      dispatch(getCustomerDetails({ accountId }))
    }
    dispatch(
      api.util.invalidateTags([
        CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
        CACHE_TAG_ACCOUNT_RECEIVABLES_LIST,
        { type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId },
        { type: CACHE_TAG_ACCOUNT_PAYMENT_LIST, id: accountId },
        { type: CACHE_TAG_ACCOUNT_EMAIL_EVENT_LIST, id: accountId },
        CACHE_TAG_ACCOUNT_LIST,
        { type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId },
        CACHE_TAG_ACCOUNT_GROUP_DETAILS,
        CACHE_TAG_AGING_GROUP_DETAILS,
        CACHE_TAG_SUSPENSION_GROUP_DETAILS,
      ])
    )
  }

  const handleRevertConfirmation = () => {
    dispatch(putIsLoading(true))
    markPaymentAsReverted({ paymentNumber, force: false })
      .unwrap()
      .then(() => {
        setIsConfirmDialogOpen(false)
        toast.info(T.PAYMENT_REVERT_MSG, {
          hideProgressBar: false,
          autoClose: 4000,
          onClose: () => invalidateAccountTransaction(),
        })
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleDownloadPaymentReceipt = () => {
    dispatch(putIsLoading(true))
    setTimeout(() => {
      downloadPaymentReceiptBlobListInChunks(businessId, [{ paymentNumber, accountTransactionHistoryId, accountId }])
      onClose()
      dispatch(putIsLoading(false))
    }, 5000)
  }

  const handleRegeneratePaymentReceipt = () => {
    dispatch(putIsLoading(true))
    regeneratePaymentReceipts({ accountTransactionHistoryIds: [accountTransactionHistoryId] })
      .unwrap()
      .then(() => toast.success(T.RECEIPT_IS_REGENERATING, { hideProgressBar: false, autoClose: 4000 }))
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleSendPaymentReceipt = () => setIsOpenReceiptConfirmationDialog(true)

  const handleRemovePaymentFromGroup = () => {
    dispatch(putIsLoading(true))
    deletePaymentBatchesFromGroup({ id: groupId, batchIds: [batchId] })
      .unwrap()
      .then(() => {
        setIsConfirmRemovePaymentDialogOpen(false)
        toast.success(T.REMOVE_INVOICE_FROM_GROUP_SUCCESS_MSG)
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleMarkAsRevertedConfirmation = () => {
    dispatch(putIsLoading(true))
    markPaymentAsReverted({ paymentNumber, force: true })
      .unwrap()
      .then(() => {
        setIsMarkAsRefundedConfirmDialogOpen(false)
        toast.success(T.PAYMENT_MARKED_AS_REVERSED_SUCCESSFULLY)
        invalidateAccountTransaction()
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleCCRefundConfirmation = () => {
    dispatch(putIsLoading(true))
    refundPayment({ paymentNumber, force: false, failed: false, refundCreditTransaction: true })
      .unwrap()
      .then(() => {
        setIsCCRefundConfirmDialogOpen(false)
        toast.success('Payment refunded successfully')
        invalidateAccountTransaction()
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handlePartialRefundConfirmation = () => {
    dispatch(putIsLoading(true))
    const refundAmountCents = Math.round(parseFloat(partialRefundAmount) * 100)
    partialRefundPayment({ paymentNumber, amountCents: refundAmountCents, note: partialRefundNote || undefined })
      .unwrap()
      .then(() => {
        setIsPartialRefundConfirmDialogOpen(false)
        setPartialRefundAmount('')
        setPartialRefundNote('')
        toast.success('Payment partially refunded successfully')
        invalidateAccountTransaction()
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleVoidConfirmation = () => {
    dispatch(putIsLoading(true))
    voidPayment({ paymentNumber })
      .unwrap()
      .then(() => {
        setIsVoidConfirmDialogOpen(false)
        toast.success(T.PAYMENT_VOIDED_SUCCESSFULLY)
        invalidateAccountTransaction()
      })
      .catch(handleError)
      .finally(() => {
        onClose()
        dispatch(putIsLoading(false))
      })
  }

  const handleCloseVoidDialog = () => {
    setIsVoidConfirmDialogOpen(false)
    onClose()
  }

  const openMarkAsRefundedConfirmationDialog = () => setIsMarkAsRefundedConfirmDialogOpen(true)

  const openCCRefundConfirmationDialog = () => setIsCCRefundConfirmDialogOpen(true)

  const openPartialRefundConfirmationDialog = () => {
    const amount = (maxPartialRefundCents / 100).toFixed(2)
    setPartialRefundAmount(amount)
    setPartialRefundNote('')
    setIsPartialRefundConfirmDialogOpen(true)
  }

  const handleOpenAddToGroupDialog = () => setIsOpenAddToGroupDialog(true)

  const handleCloseAddToGroupDialog = () => {
    setIsOpenAddToGroupDialog(false)
    onClose()
  }

  const handleOpenAddUpdatePaymentNote = () => setIsOpenAddUpdatePaymentNoteDialog(true)

  const handleCloseAddUpdatePaymentNote = () => {
    setIsOpenAddUpdatePaymentNoteDialog(false)
    onClose()
  }

  const handleCloseMarkAsRevertedDialog = () => {
    setIsMarkAsRefundedConfirmDialogOpen(false)
    onClose()
  }

  const handleCloseCCRefundDialog = () => {
    setIsCCRefundConfirmDialogOpen(false)
    onClose()
  }

  const handleClosePartialRefundDialog = () => {
    setIsPartialRefundConfirmDialogOpen(false)
    setPartialRefundAmount('')
    setPartialRefundNote('')
    onClose()
  }

  const handleCloseRevertConfirmDialog = () => {
    if (!canDismissRevertDialog) return
    setIsConfirmDialogOpen(false)
    onClose()
  }

  const handleCloseTransferPaymentModal = () => {
    setTransferInvoiceDialog({ isOpen: false, isIntraAccountTransfer: false })
    onClose()
  }

  const handleCloseACHFailedDialog = () => {
    setIsACHDialogDialogOpen(false)
    onClose()
  }

  const handleCloseRemovePaymentFromGroupDialog = () => {
    setIsConfirmRemovePaymentDialogOpen(false)
    onClose()
  }

  const handleCloseReceiptConfirmationDialog = () => {
    setIsOpenReceiptConfirmationDialog(false)
    onClose()
  }

  const handleCloseMoveToAccountCreditConfirmation = () => {
    setIsOpenMoveToAccountCreditConfirmation(false)
    onClose()
  }

  const actionTooltipProps = { tooltip: { sx: { fontSize: '0.875rem', py: 1, px: 1.5 } } }

  return (
    <>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ variant: 'outlined' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        disableScrollLock
      >
        {paymentNumber && (
          <MenuItem onClick={handleOpenAddUpdatePaymentNote}>{`${paymentNote ? 'Edit' : 'Add'} ${lowerCase(T.PAYMENT_NOTE)}`}</MenuItem>
        )}
        {paymentNumber && <MenuItem onClick={handleDownloadPaymentReceipt}>{T.DOWNLOAD}</MenuItem>}
        {paymentNumber && <MenuItem onClick={handleRegeneratePaymentReceipt}>{T.REGENERATE_RECEIPT}</MenuItem>}
        {paymentNumber && <MenuItem onClick={handleSendPaymentReceipt}>{T.EMAIL}</MenuItem>}
        {showAddToGroupAction && <MenuItem onClick={handleOpenAddToGroupDialog}>{T.ADD_TO_PAYMENT_GROUP}</MenuItem>}
        {groupId && <MenuItem onClick={() => setIsConfirmRemovePaymentDialogOpen(true)}>{T.REMOVE_FROM_GROUP}</MenuItem>}
        {showTransferAction && paymentNumber && isRevertTransferBtnEnabled && linkedInvoices.length > 0 && (
          <>
            <BillingLockedTooltip isLocked={isLocked}>
              <Tooltip title={T.PAYMENT_ALREADY_REVERT_TRANSFER_MSG} disableHoverListener={isDuplicateActionTooltipDisabled} slotProps={actionTooltipProps}>
                <span>
                  <MenuItem
                    sx={{ color: 'warning.main' }}
                    disabled={isDuplicateActionDisabled}
                    onClick={() => setTransferInvoiceDialog({ isOpen: true, isIntraAccountTransfer: false })}
                  >
                    {T.TRANSFER_TO_INVOICE}
                  </MenuItem>
                </span>
              </Tooltip>
            </BillingLockedTooltip>

            <BillingLockedTooltip isLocked={isLocked}>
              <Tooltip title={T.PAYMENT_ALREADY_REVERT_TRANSFER_MSG} disableHoverListener={isDuplicateActionTooltipDisabled} slotProps={actionTooltipProps}>
                <span>
                  <MenuItem
                    sx={{ color: 'warning.main' }}
                    disabled={isDuplicateActionDisabled}
                    onClick={() => setTransferInvoiceDialog({ isOpen: true, isIntraAccountTransfer: true })}
                  >
                    {T.TRANSFER_TO_ACCOUNT}
                  </MenuItem>
                </span>
              </Tooltip>
            </BillingLockedTooltip>
          </>
        )}

        {showACHFailedAction && (isMarkACHFailedBtnEnabled || isMarkAsFailedBtnEnabledFortis) && (
          <BillingLockedTooltip isLocked={isLocked}>
            <Tooltip title={T.PAYMENT_ALREADY_ACH_FAILED_MSG} disableHoverListener={isDuplicateActionTooltipDisabled} slotProps={actionTooltipProps}>
              <span>
                <MenuItem sx={{ color: 'error.main' }} disabled={isDuplicateActionDisabled} onClick={() => setIsACHDialogDialogOpen(true)}>
                  {T.ACH_FAILED}
                </MenuItem>
              </span>
            </Tooltip>
          </BillingLockedTooltip>
        )}
        {showRevertAction && paymentNumber && isRevertTransferBtnEnabled && (
          <BillingLockedTooltip isLocked={isLocked}>
            <Tooltip
              title={hasPartialRefunds ? T.PAYMENT_HAS_PARTIAL_REFUNDS_MSG : T.PAYMENT_ALREADY_REVERT_TRANSFER_MSG}
              disableHoverListener={!hasPartialRefunds && isDuplicateActionTooltipDisabled}
              slotProps={actionTooltipProps}
            >
              <span>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  disabled={isDuplicateActionDisabled || hasPartialRefunds}
                  onClick={() => setIsConfirmDialogOpen(true)}
                >
                  {T.REVERT}
                </MenuItem>
              </span>
            </Tooltip>
          </BillingLockedTooltip>
        )}
        {paymentNumber && isMarkAsRevertedBtnEnabled && (
          <BillingLockedTooltip isLocked={isLocked}>
            <Tooltip
              title={hasPartialRefunds ? T.PAYMENT_HAS_PARTIAL_REFUNDS_MSG : T.PAYMENT_ALREADY_REVERT_TRANSFER_MSG}
              disableHoverListener={!hasPartialRefunds && isDuplicateActionTooltipDisabled}
              slotProps={actionTooltipProps}
            >
              <span>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  disabled={isDuplicateActionDisabled || hasPartialRefunds}
                  onClick={openMarkAsRefundedConfirmationDialog}
                >
                  {T.MARK_AS_REVERTED}
                </MenuItem>
              </span>
            </Tooltip>
          </BillingLockedTooltip>
        )}
        {paymentNumber && isVoidBtnEnabled && (
          <BillingLockedTooltip isLocked={isLocked}>
            <span>
              <MenuItem
                sx={{ color: 'error.main' }}
                disabled={isLocked}
                onClick={() => setIsVoidConfirmDialogOpen(true)}
              >
                {T.VOID_PAYMENT}
              </MenuItem>
            </span>
          </BillingLockedTooltip>
        )}
        {paymentNumber && isCCRefundBtnEnabled && (
          <BillingLockedTooltip isLocked={isLocked}>
            <Tooltip
              title={hasPartialRefunds ? T.PAYMENT_HAS_PARTIAL_REFUNDS_MSG : T.PAYMENT_ALREADY_REVERT_TRANSFER_MSG}
              disableHoverListener={!hasPartialRefunds && isDuplicateActionTooltipDisabled}
              slotProps={actionTooltipProps}
            >
              <span>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  disabled={isDuplicateActionDisabled || hasPartialRefunds}
                  onClick={openCCRefundConfirmationDialog}
                >
                  {T.FULL_REFUND}
                </MenuItem>
              </span>
            </Tooltip>
          </BillingLockedTooltip>
        )}
        {paymentNumber && isPartialRefundEnabled && (
          <BillingLockedTooltip isLocked={isLocked}>
            <Tooltip
              title={
                maxPartialRefundCents <= 0
                  ? availableAccountCreditCents <= 0
                    ? 'No available account credit for partial refund'
                    : 'Payment fully refunded'
                  : ''
              }
              disableHoverListener={maxPartialRefundCents > 0}
              slotProps={actionTooltipProps}
            >
              <span>
                <MenuItem
                  sx={{ color: 'error.main' }}
                  disabled={isLocked || maxPartialRefundCents <= 0}
                  onClick={openPartialRefundConfirmationDialog}
                >
                  {T.PARTIAL_REFUND_PAYMENT}
                </MenuItem>
              </span>
            </Tooltip>
          </BillingLockedTooltip>
        )}
      </Menu>

      <HHConfirmDialog
        isOpen={isMarkAsRefundedConfirmDialogOpen}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        confirmButtonTitle={T.MARK_AS_REVERTED}
        confirmTitle={T.MARK_AS_REVERTED}
        confirmDescription={
          <>
            <Typography variant="h6" fontWeight={400}>
              {`Payment ${paymentNumber} will be marked as reversed. Do you want to proceed?`}
            </Typography>
          </>
        }
        onConfirm={handleMarkAsRevertedConfirmation}
        onClose={handleCloseMarkAsRevertedDialog}
      />

      <HHConfirmDialog
        isOpen={isCCRefundConfirmDialogOpen}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        ConfirmButtonProps={{ disabled: !isFortis }}
        confirmButtonTitle={T.FULL_REFUND}
        confirmTitle={T.FULL_REFUND}
        confirmDescription={
          <>
            <Typography variant="h6" fontWeight={400}>
              {isFortis
                ? lastFourDigits
                  ? <><strong>{paymentTypeDisplay} ending in {lastFourDigits}</strong> will be fully refunded. Do you want to proceed?</>
                  : `Payment ${paymentNumber} will be fully refunded to the original payment method. Do you want to proceed?`
                : T.REFUND_NOT_SUPPORTED}
            </Typography>
          </>
        }
        onConfirm={handleCCRefundConfirmation}
        onClose={handleCloseCCRefundDialog}
      />

      <HHConfirmDialog
        isOpen={isVoidConfirmDialogOpen}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        confirmButtonTitle={T.VOID_PAYMENT}
        confirmTitle={T.VOID_PAYMENT}
        confirmDescription={
          <>
            <Typography variant="h6" fontWeight={400}>
              {lastFourDigits
                ? <><strong>{paymentTypeDisplay} ending in {lastFourDigits}</strong> will be voided. This cancels the transaction before settlement. Do you want to proceed?</>
                : `Payment ${paymentNumber} will be voided. This cancels the transaction before settlement. Do you want to proceed?`}
            </Typography>
          </>
        }
        onConfirm={handleVoidConfirmation}
        onClose={handleCloseVoidDialog}
      />

      <HHConfirmDialog
        isOpen={isPartialRefundConfirmDialogOpen}
        DialogProps={{ maxWidth: 'md', fullWidth: true }}
        ConfirmButtonProps={{ disabled: !isFortis || !isValidRefundAmount || maxPartialRefundCents <= 0 }}
        confirmButtonTitle={T.REFUND_PAYMENT}
        confirmTitle={T.REFUND_PAYMENT}
        confirmDescription={
          <>
            {isFortis ? (
              <>
                <Typography variant="h5" fontWeight={400} mb={3}>
                  {lastFourDigits
                    ? <><strong>{paymentTypeDisplay} ending in {lastFourDigits}</strong> will be partially refunded.</>
                    : `Payment ${paymentNumber} will be partially refunded to the original payment method.`}
                </Typography>

                {maxPartialRefundCents <= 0 ? (
                  <Box>
                    <Typography variant="h6" color="error.main" fontWeight={500} mb={1}>
                      Partial refund not available
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {availableAccountCreditCents <= 0
                        ? 'No available account credit. Please create an account credit before issuing a partial refund.'
                        : 'This payment has been fully refunded.'}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box mb={3} p={3} sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box display="flex" justifyContent="space-between" mb={1.5}>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                          Payment amount
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                          ${(paymentAmountCents / 100).toFixed(2)}
                        </Typography>
                      </Box>
                      {totalRefundedCents > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1.5}>
                          <Typography variant="body1" color="text.secondary">
                            Previously refunded
                          </Typography>
                          <Typography variant="body1" color="error.main">
                            -${(totalRefundedCents / 100).toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      <Box display="flex" justifyContent="space-between" mb={1.5}>
                        <Typography variant="body1" color="text.secondary">
                          Remaining refundable
                        </Typography>
                        <Typography variant="body1" color="text.primary" fontWeight={500}>
                          ${(remainingRefundableAmountCents / 100).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" pt={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body1" color="text.secondary">
                          Available account credit
                        </Typography>
                        <Typography variant="body1" color="text.primary" fontWeight={500}>
                          ${(availableAccountCreditCents / 100).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="primary.main" mb={3} sx={{ fontStyle: 'italic' }}>
                      Partial refunds are limited by available account credit to preserve invoice allocations.
                    </Typography>

                    <Box mb={3}>
                      <TextField
                        label="Refund Amount"
                        type="number"
                        fullWidth
                        autoFocus
                        value={partialRefundAmount}
                        onChange={e => setPartialRefundAmount(e.target.value)}
                        inputProps={{
                          min: 0.01,
                          max: maxPartialRefundCents / 100,
                          step: 0.01,
                        }}
                        helperText={refundAmountError || `Maximum: $${maxPartialRefundAmount}`}
                        error={!!refundAmountError}
                      />
                    </Box>

                    <Box>
                      <TextField
                        label="Refund Note (Optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={partialRefundNote}
                        onChange={e => setPartialRefundNote(e.target.value)}
                        placeholder="Add a note about this partial refund..."
                        helperText="This note will be saved with the refund transaction"
                      />
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography variant="h6" fontWeight={400}>
                {T.REFUND_NOT_SUPPORTED}
              </Typography>
            )}
          </>
        }
        onConfirm={handlePartialRefundConfirmation}
        onClose={handleClosePartialRefundDialog}
      />

      <HHConfirmDialog
        isOpen={isConfirmDialogOpen}
        DialogProps={{ maxWidth: 'md', fullWidth: true, disableEscapeKeyDown: !canDismissRevertDialog }}
        ConfirmButtonProps={{ disabled: !canDismissRevertDialog }}
        confirmButtonTitle={T.REVERT_CONFIRM_BUTTON}
        confirmTitle="Revert payment"
        confirmDescription={
          <>
            <Typography variant="h6" fontWeight={600} color="warning.main">
              Warning: Move the payment to account credit first or you may create{' '}
              <Typography component="strong" fontWeight={600} sx={{ textDecoration: 'underline' }}>
                negative account credit
              </Typography>
              .
            </Typography>
            <Typography variant="h6" fontWeight={400} mt={2}>
              {revertMessage}
            </Typography>
          </>
        }
        onConfirm={handleRevertConfirmation}
        onClose={handleCloseRevertConfirmDialog}
      />

      <TransferPaymentModal
        isOpen={transferInvoiceDialog.isOpen}
        customerDetails={customerDetails}
        isIntraAccountTransfer={transferInvoiceDialog.isIntraAccountTransfer}
        payment={selectedPayment}
        onClose={handleCloseTransferPaymentModal}
      />

      <ACHFailedDialog
        isOpen={isACHDialogDialogOpen}
        customerDetails={customerDetails}
        payment={selectedPayment}
        onClose={handleCloseACHFailedDialog}
      />
      <HHConfirmDialog
        isOpen={isConfirmRemovePaymentDialogOpen}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        ConfirmButtonProps={{ color: 'warning' }}
        confirmButtonTitle={T.REMOVE}
        confirmTitle="Remove payment"
        confirmDescription={`By removing this payment from this group, the other payments within the batch ${batchId} will also be removed. Do you want to proceed?`}
        onConfirm={handleRemovePaymentFromGroup}
        onClose={handleCloseRemovePaymentFromGroupDialog}
      />
      <AddToGroupDialog isOpen={isOpenAddToGroupDialog} batchIds={[batchId]} onClose={handleCloseAddToGroupDialog} />
      {isOpenReceiptConfirmationDialog && (
        <PaymentReceiptEmailConfirmation
          isOpen={isOpenReceiptConfirmationDialog}
          isSingleAccount
          payments={[selectedPayment]}
          onClose={handleCloseReceiptConfirmationDialog}
        />
      )}
      <MoveToAccountCreditConfirmation
        isOpen={isOpenMoveToAccountCreditConfirmation}
        accountId={accountId}
        invoiceNumbers={get(selectedPayment, 'invoices', []).map(({ invoiceNumber }) => invoiceNumber)}
        paymentNumbers={paymentNumber ? [paymentNumber] : []}
        onClose={handleCloseMoveToAccountCreditConfirmation}
      />
      <AddUpdatePaymentNoteDialog
        isOpen={isOpenAddUpdatePaymentNoteDialog}
        accountId={accountId}
        paymentNumber={paymentNumber}
        existingNote={paymentNote}
        onClose={handleCloseAddUpdatePaymentNote}
      />
    </>
  )
}

PaymentsActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  customerDetails: PropTypes.object,
  selectedPayment: PropTypes.object,
  isOriginalPaymentReversedOrTransferred: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  showRevertAction: PropTypes.bool,
  showTransferAction: PropTypes.bool,
  showACHFailedAction: PropTypes.bool,
  showAddToGroupAction: PropTypes.bool,
  showMoveToAccountCreditAction: PropTypes.bool,
}

export default PaymentsActionMenu
